import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import { twoFactorApi } from '../lib/api'
import agmsLogo from '../assets/logos/agms-logo-on-white.svg'

export function SetupTwoFactorPage() {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [confirmedCodes, setConfirmedCodes] = useState<string[] | null>(null)
  const { data, isLoading, error } = useQuery({
    queryKey: ['2fa-setup'],
    queryFn: twoFactorApi.setup,
    retry: false,
  })

  const confirmMutation = useMutation({
    mutationFn: () => twoFactorApi.confirm(code),
    onSuccess: (response) => {
      setConfirmedCodes(response.recovery_codes)
    },
  })

  useEffect(() => {
    document.title = 'Set up two-factor authentication · AGMS CRM'
  }, [])

  const downloadRecoveryCodes = () => {
    if (!confirmedCodes) return
    const blob = new Blob([confirmedCodes.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'agms-crm-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-2xl overflow-hidden">
        <div className="h-1 bg-accent-500" />
        <CardHeader className="items-center text-center">
          <img src={agmsLogo} alt="AGMS" className="h-12 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Set up two-factor authentication</h1>
          <p className="mt-2 text-sm text-gray-600">Scan the QR code with your authenticator app, then enter the six-digit code.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <CardTitle>Authenticator App</CardTitle>
          {isLoading && <p className="text-sm text-gray-500">Preparing setup...</p>}
          {error && <p className="text-sm text-red-600">Unable to load 2FA setup.</p>}
          {data && (
            <>
              <div className="grid gap-5 md:grid-cols-[220px,1fr]">
                <div className="rounded-md border border-slate-200 bg-white p-3" dangerouslySetInnerHTML={{ __html: data.qr_svg }} />
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-medium uppercase text-gray-500">Manual Secret</div>
                    <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-sm text-gray-900 break-all">
                      {data.manual_secret}
                    </div>
                  </div>
                  <Input
                    label="Six-digit code"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                  />
                  {confirmMutation.error && (
                    <p className="text-sm text-red-600">Invalid code. Check your authenticator app and try again.</p>
                  )}
                  <Button
                    type="button"
                    onClick={() => confirmMutation.mutate()}
                    isLoading={confirmMutation.isPending}
                    disabled={code.trim().length < 6}
                  >
                    Confirm 2FA
                  </Button>
                </div>
              </div>

              {confirmedCodes && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                  <div className="font-semibold text-emerald-950">2FA confirmed</div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {confirmedCodes.map((item) => (
                      <code key={item} className="rounded bg-white px-2 py-1 text-sm text-gray-900">{item}</code>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" onClick={downloadRecoveryCodes}>
                      Download Codes
                    </Button>
                    <Button type="button" onClick={() => navigate('/dashboard', { replace: true })}>
                      Continue
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
